import type { FC } from "react";
import { useState } from "react";

import RiskUpdateModal from "@/components/modals/profil-risk/risk-update-modal";
import type { PatientProps } from "@/components/patient/patient-profile";
import PatientProfilePart from "@/components/patient/patient-profile-part";
import RiskFactorDropButton from "@/components/patient/risk-factor-drop-button";
import type { Risk } from "@/utils/patient";
import {
  getDiabetLabel,
  getSmokingPatientLabel,
  getToothBrushHairType,
  getToothBrushType,
} from "@/utils/patient";

const PatientRiskFactors: FC<PatientProps> = ({ patient }) => {
  const [riskModalOpen, setRiskModalOpen] = useState<Risk | "none">("none");

  return (
    <PatientProfilePart title={"Facteurs de risque"}>
      <RiskFactorDropButton
        state={patient.toothState}
        isOpenByDefault={
          patient.toothState !== "NONE" ||
          patient.brushingFrequency !== null ||
          patient.toothBrush !== null ||
          patient.toothBrushHair !== null ||
          patient.toothPaste !== null ||
          patient.brushingAccessories !== null ||
          patient.interdentalSystem ||
          patient.mouthWash !== null
        }
        risk={"toothbrush"}
        setRiskModalOpen={setRiskModalOpen}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500">Fréquence</div>
            <div className="text-sm">{patient.brushingFrequency ?? 0}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Brosse à dents</div>
            <div className="text-sm">{`${getToothBrushType(patient.toothBrush)} ${patient.toothBrush ? "&" : ""} ${getToothBrushHairType(patient.toothBrushHair)}`}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Système interdentaire</div>
            <div className="text-sm">
              {patient.interdentalSystem ? "Oui" : "Non"}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Accessoires de brossage</div>
            <div className="text-sm">{patient.brushingAccessories}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Dentifrice</div>
            <div className="text-sm">{patient.toothPaste}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Bain de bouche</div>
            <div className="text-sm">{patient.mouthWash}</div>
          </div>
        </div>
      </RiskFactorDropButton>
      <RiskFactorDropButton
        state={patient.smokingState}
        isOpenByDefault={
          patient.smokingState !== "NONE" ||
          patient.smoking !== "FALSE" ||
          patient.smokingEnd !== null ||
          patient.dailySmokes !== null
        }
        risk={"smoking"}
        setRiskModalOpen={setRiskModalOpen}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500">Patient fumeur</div>
            <div className="text-sm">
              {getSmokingPatientLabel(patient.smoking)}
            </div>
          </div>
          {patient.smoking === "PREVIOUS" ? (
            <>
              <div>
                <div className="text-xs text-gray-500">Date d'arrêt</div>
                <div className="text-sm">
                  {patient.smokingEnd
                    ? new Date(patient.smokingEnd).toLocaleDateString()
                    : ""}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">
                  Consommation avant arrêt
                </div>
                <div className="text-sm">{patient.dailySmokes?.toString()}</div>
              </div>
            </>
          ) : null}
          {patient.smoking === "TRUE" ? (
            <div>
              <div className="text-xs text-gray-500">Consommation</div>
              <div className="text-sm">{patient.dailySmokes?.toString()}</div>
            </div>
          ) : null}
        </div>
      </RiskFactorDropButton>
      <RiskFactorDropButton
        state={patient.diabetState}
        isOpenByDefault={
          patient.diabetState !== "NONE" ||
          patient.diabetic !== "NONE" ||
          patient.diabetComment !== null
        }
        risk={"diabetic"}
        setRiskModalOpen={setRiskModalOpen}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500">Patient diabétique</div>
            <div className="text-sm">{getDiabetLabel(patient.diabetic)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Commentaire</div>
            <div className="text-sm">{patient.diabetComment}</div>
          </div>
        </div>
      </RiskFactorDropButton>
      <RiskFactorDropButton
        state={patient.stressState}
        isOpenByDefault={
          patient.stressState !== "NONE" ||
          patient.stress !== 1 ||
          patient.stressComment !== null
        }
        risk={"stress"}
        setRiskModalOpen={setRiskModalOpen}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500">Niveau de stress</div>
            <div className="text-sm">{`${patient.stress?.toString()}/10`}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Commentaire</div>
            <div className="text-sm">{patient.stressComment}</div>
          </div>
        </div>
      </RiskFactorDropButton>
      <RiskFactorDropButton
        state={patient.geneticState}
        isOpenByDefault={
          patient.geneticState !== "NONE" ||
          patient.geneticTendency ||
          patient.geneticComment !== null
        }
        risk={"geneticTendency"}
        setRiskModalOpen={setRiskModalOpen}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500">
              Patient prédisposé génétiquement
            </div>
            <div className="text-sm">
              {patient.geneticTendency ? "Oui" : "Non"}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Commentaire</div>
            <div className="text-sm">{patient.geneticComment}</div>
          </div>
        </div>
      </RiskFactorDropButton>
      <RiskUpdateModal
        riskModalOpen={riskModalOpen}
        setRiskModalOpen={setRiskModalOpen}
        patient={patient}
      />
    </PatientProfilePart>
  );
};

export default PatientRiskFactors;
